/**
 * BackupPage.js  -  /tasks/files/backup
 *
 * Deleted files waiting to be restored, and the log of every change made in
 * Files. For Top Management, managers, the super admin, the CEO and the COO.
 *
 * The server enforces that: anybody else asking for either list gets a 403,
 * whatever this page does. The page only decides what to draw.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Stack,
  Chip,
  Button,
  IconButton,
  Typography,
  Tooltip,
  TextField,
  MenuItem,
  Alert,
  Skeleton,
  Container,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Breadcrumbs,
  Link,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  useTheme,
  alpha,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { tokens } from "../../../theme";
import { buildUi, fmtDate } from "../taskShared";
import {
  filesCall,
  fileLink,
  downloadFile,
  fileIcon,
  kindColor,
  fmtBytes,
  isoish,
} from "./filesApi";
import { PreviewDialog } from "./FolderPage";

import RestoreOutlinedIcon from "@mui/icons-material/RestoreOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";

/** How each logged action reads, and its colour. */
const ACTIONS = {
  create_folder: { label: "Folder created", color: "#1565c0" },
  delete_folder: { label: "Folder deleted", color: "#6d4c41" },
  upload: { label: "Uploaded", color: "#2e7d32" },
  delete_file: { label: "Deleted", color: "#c62828" },
  restore_file: { label: "Restored", color: "#8e24aa" },
  purge_file: { label: "Deleted permanently", color: "#b71c1c" },
  share: { label: "Sharing changed", color: "#ef6c00" },
};

/** A short, human summary of a log entry's details. */
const describe = (e) => {
  const d = e.details || {};
  const bits = [];
  if (d.size != null) bits.push(fmtBytes(d.size));
  if (e.action === "share") {
    const before = (d.before || []).join(", ") || "nobody";
    const after = (d.after || []).join(", ") || "nobody";
    bits.push(`${before} → ${after}`);
  }
  if (e.action === "restore_file" && d.deleted_from && d.restored_to !== d.deleted_from) {
    bits.push(`was in ${d.deleted_from}`);
  }
  if (e.action === "delete_folder") {
    if (d.deleted_folder) bits.push(`removed ${d.deleted_folder}`);
    if (d.backups_moved_to_parent) bits.push(`${d.backups_moved_to_parent} backed-up file(s) moved up`);
  }
  if (e.action === "purge_file") {
    if (d.deleted_from) bits.push(`was in ${d.deleted_from}`);
    if (d.uploaded_by) bits.push(`uploaded by ${d.uploaded_by}`);
    if (d.deleted_by) bits.push(`first deleted by ${d.deleted_by}`);
  }
  if (d.notified != null) bits.push(`${d.notified} emailed`);
  return bits.join(" · ");
};

/* ------------------------------------------------------------------ */

const DeletedRow = ({ ui, file, canPurge, onPreview, onDownload, onRestore, onPurge }) => {
  const moved = file.restore_to && file.deleted_from && file.restore_to !== file.deleted_from;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        py: 1.3,
        px: 1,
        borderBottom: `1px solid ${alpha(ui.border, 0.7)}`,
        flexWrap: { xs: "wrap", sm: "nowrap" },
        "&:last-of-type": { borderBottom: "none" },
      }}
    >
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: "10px",
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
          color: kindColor(file.kind),
          bgcolor: alpha(kindColor(file.kind), 0.12),
        }}
      >
        {fileIcon(file, { fontSize: 21 })}
      </Box>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" fontWeight={700} color={ui.textPrimary} noWrap title={file.original_name}>
          {file.original_name}
        </Typography>
        <Typography variant="caption" color={ui.textSecondary} component="div">
          {fmtBytes(file.size_bytes)} · deleted by {file.deleted_by || "unknown"} on{" "}
          {fmtDate(isoish(file.deleted_at))} · from <strong>{file.deleted_from || "—"}</strong>
        </Typography>
        {moved && (
          <Typography variant="caption" sx={{ color: "#ef6c00" }} component="div">
            Its original folder no longer exists - restoring puts it in {file.restore_to}
          </Typography>
        )}
      </Box>

      <Stack direction="row" spacing={0.3} sx={{ flexShrink: 0 }}>
        {file.previewable && (
          <Tooltip title="Preview">
            <IconButton size="small" onClick={onPreview}>
              <VisibilityOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Download">
          <IconButton size="small" onClick={onDownload}>
            <DownloadOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Button size="small" variant="outlined" startIcon={<RestoreOutlinedIcon />} onClick={onRestore}>
          Restore
        </Button>
        {canPurge && (
          <Tooltip title="Delete permanently - cannot be undone">
            <IconButton size="small" color="error" onClick={onPurge}>
              <DeleteForeverOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
};

const LogRow = ({ ui, entry }) => {
  const a = ACTIONS[entry.action] || { label: entry.action, color: "#607d8b" };
  const extra = describe(entry);
  return (
    <Box
      sx={{
        display: "flex",
        gap: 1.5,
        py: 1.2,
        px: 1,
        borderBottom: `1px solid ${alpha(ui.border, 0.7)}`,
        alignItems: "flex-start",
        "&:last-of-type": { borderBottom: "none" },
      }}
    >
      <Chip
        size="small"
        label={a.label}
        sx={{ fontWeight: 700, color: a.color, bgcolor: alpha(a.color, 0.12), minWidth: 118 }}
      />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" color={ui.textPrimary}>
          <strong>{entry.actor_name || "Someone"}</strong>
          {entry.file_name ? (
            <>
              {" · "}
              <span title={entry.file_name}>{entry.file_name}</span>
            </>
          ) : null}
        </Typography>
        <Typography variant="caption" color={ui.textSecondary} component="div">
          {entry.folder_path || "—"}
          {extra ? ` · ${extra}` : ""}
        </Typography>
      </Box>
      <Typography variant="caption" color={ui.textSecondary} sx={{ whiteSpace: "nowrap" }}>
        {fmtDate(isoish(entry.createDate))} {String(entry.createDate || "").slice(11, 16)}
      </Typography>
    </Box>
  );
};

/* ================================================================== */

const BackupPage = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const ui = useMemo(() => buildUi(colors, isDark), [colors, isDark]);
  const navigate = useNavigate();

  const [tab, setTab] = useState(0);

  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  const [entries, setEntries] = useState([]);
  const [loadingLog, setLoadingLog] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [filter, setFilter] = useState("");

  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoring, setRestoring] = useState(false);

  // From the server: management roles only. The server checks again on purge.
  const [canPurge, setCanPurge] = useState(false);
  const [purgeTarget, setPurgeTarget] = useState(null);
  const [purgeConfirmed, setPurgeConfirmed] = useState(false);
  const [purging, setPurging] = useState(false);
  const [preview, setPreview] = useState(null);

  /* ---------------------------------------------------- deleted files */

  const loadFiles = useCallback(async () => {
    setLoadingFiles(true);
    const res = await filesCall("backup");
    setLoadingFiles(false);
    if (!res?.success) {
      setError(res?.message || "Could not open the backup");
      return;
    }
    setError("");
    setFiles(res.data.files || []);
    setCanPurge(!!res.data.can_purge);
  }, []);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return files;
    return files.filter(
      (f) =>
        f.original_name.toLowerCase().includes(q) ||
        String(f.deleted_from || "").toLowerCase().includes(q) ||
        String(f.deleted_by || "").toLowerCase().includes(q)
    );
  }, [files, search]);

  /* ------------------------------------------------------ activity log */

  const loadLog = useCallback(
    async (before = 0) => {
      setLoadingLog(true);
      const res = await filesCall("activity", { filter, before: before || undefined });
      setLoadingLog(false);
      if (!res?.success) {
        toast.error(res?.message || "Could not load the activity log");
        return;
      }
      const page = res.data.entries || [];
      setEntries((cur) => (before ? [...cur, ...page] : page));
      setHasMore(!!res.data.has_more);
    },
    [filter]
  );

  useEffect(() => {
    if (tab === 1) loadLog(0);
  }, [tab, loadLog]);

  /* --------------------------------------------------------- actions */

  const doRestore = async () => {
    if (!restoreTarget) return;
    setRestoring(true);
    const res = await filesCall("restore", { id: restoreTarget.id });
    setRestoring(false);
    setRestoreTarget(null);
    if (!res?.success) {
      toast.error(res?.message || "Could not restore the file");
      return;
    }
    const told = res.data?.notified;
    toast.success(`${res.message}${told ? ` · ${told} member(s) emailed` : ""}`);
    loadFiles();
  };

  const askPurge = (file) => {
    setPurgeConfirmed(false);   // never carried over from a previous file
    setPurgeTarget(file);
  };

  const doPurge = async () => {
    if (!purgeTarget || !purgeConfirmed) return;
    setPurging(true);
    const res = await filesCall("purge", { id: purgeTarget.id });
    setPurging(false);
    setPurgeTarget(null);
    setPurgeConfirmed(false);
    if (!res?.success) {
      toast.error(res?.message || "Could not delete the file");
      return;
    }
    toast.success(res.message);
    loadFiles();
  };

  const openPreview = async (file) => {
    setPreview({ file, loading: true });
    const { url, message } = await fileLink(file.id, false);
    setPreview(url ? { file, url } : { file, error: message || "This file cannot be previewed" });
  };

  const download = async (file) => {
    const msg = await downloadFile(file.id);
    if (msg) toast.error(msg);
  };

  /* ================================================================ */

  return (
    <Box sx={{ bgcolor: ui.pageBg, minHeight: "100%", py: 3 }}>
      <ToastContainer position="top-right" autoClose={4000} theme={isDark ? "dark" : "light"} />
      <Container maxWidth="xl">
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
          <Link component="button" underline="hover" color="inherit" onClick={() => navigate("/tasks/files")}>
            Files
          </Link>
          <Typography color={ui.textPrimary} fontWeight={700}>
            Backup
          </Typography>
        </Breadcrumbs>

        <Box
          sx={{
            background: ui.gradient,
            borderRadius: "16px",
            p: { xs: 2.5, md: 3.5 },
            mb: 3,
            color: "#fff",
            boxShadow: ui.shadow,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Inventory2OutlinedIcon sx={{ fontSize: 34 }} />
            <Box>
              <Typography variant="h4" fontWeight={800}>
                Backup
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.92, mt: 0.5 }}>
                Every deleted file is kept here until it is restored, alongside a record of
                every change made in Files
              </Typography>
            </Box>
          </Stack>
        </Box>

        {error ? (
          <Alert
            severity="warning"
            sx={{ borderRadius: "12px" }}
            action={
              <Button color="inherit" size="small" onClick={() => navigate("/tasks/files")}>
                Back to Files
              </Button>
            }
          >
            {error}
          </Alert>
        ) : (
          <>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
              <Tab label={`Deleted files (${files.length})`} />
              <Tab label="Activity log" />
            </Tabs>

            {/* ---------------- Deleted files ---------------- */}
            {tab === 0 && (
              <Box
                sx={{
                  bgcolor: ui.surface,
                  border: `1px solid ${ui.border}`,
                  borderRadius: "14px",
                  p: { xs: 1.5, md: 2.5 },
                  boxShadow: ui.shadow,
                }}
              >
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search by file, folder or who deleted it"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{ mb: 1.5 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchOutlinedIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                {loadingFiles && <Skeleton variant="rounded" height={160} />}
                {!loadingFiles &&
                  shown.map((f) => (
                    <DeletedRow
                      key={f.id}
                      ui={ui}
                      file={f}
                      onPreview={() => openPreview(f)}
                      onDownload={() => download(f)}
                      onRestore={() => setRestoreTarget(f)}
                      canPurge={canPurge}
                      onPurge={() => askPurge(f)}
                    />
                  ))}
                {!loadingFiles && shown.length === 0 && (
                  <Typography color={ui.textSecondary} sx={{ py: 4, textAlign: "center" }}>
                    {search ? "Nothing in the backup matches your search." : "The backup is empty."}
                  </Typography>
                )}
              </Box>
            )}

            {/* ---------------- Activity log ---------------- */}
            {tab === 1 && (
              <Box
                sx={{
                  bgcolor: ui.surface,
                  border: `1px solid ${ui.border}`,
                  borderRadius: "14px",
                  p: { xs: 1.5, md: 2.5 },
                  boxShadow: ui.shadow,
                }}
              >
                <TextField
                  select
                  size="small"
                  label="Show"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  sx={{ minWidth: 220, mb: 1.5 }}
                >
                  <MenuItem value="">Everything</MenuItem>
                  {Object.entries(ACTIONS).map(([k, v]) => (
                    <MenuItem key={k} value={k}>
                      {v.label}
                    </MenuItem>
                  ))}
                </TextField>

                {entries.map((e) => (
                  <LogRow key={e.id} ui={ui} entry={e} />
                ))}
                {loadingLog && <Skeleton variant="rounded" height={120} sx={{ mt: 1 }} />}
                {!loadingLog && entries.length === 0 && (
                  <Typography color={ui.textSecondary} sx={{ py: 4, textAlign: "center" }}>
                    Nothing has been logged yet.
                  </Typography>
                )}
                {hasMore && !loadingLog && (
                  <Box sx={{ textAlign: "center", mt: 2 }}>
                    <Button onClick={() => loadLog(entries[entries.length - 1]?.id || 0)}>Load older</Button>
                  </Box>
                )}
              </Box>
            )}
          </>
        )}
      </Container>

      <Dialog open={!!restoreTarget} onClose={() => setRestoreTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Restore this file?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            <strong>{restoreTarget?.original_name}</strong> will go back to{" "}
            <strong>{restoreTarget?.restore_to}</strong>, and that folder's members will be
            emailed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreTarget(null)}>Cancel</Button>
          <Button variant="contained" onClick={doRestore} disabled={restoring}>
            {restoring ? "Restoring…" : "Restore"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!purgeTarget}
        onClose={() => !purging && setPurgeTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800, color: "error.main" }}>Delete permanently?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            <strong>{purgeTarget?.original_name}</strong> will be erased from the server. It
            cannot be restored afterwards by anyone.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            The activity log keeps a record that it existed - its name, size, where it was,
            who uploaded and deleted it, and who removed it for good.
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                color="error"
                checked={purgeConfirmed}
                onChange={(e) => setPurgeConfirmed(e.target.checked)}
              />
            }
            label="I understand this cannot be undone"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPurgeTarget(null)} disabled={purging}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            startIcon={<DeleteForeverOutlinedIcon />}
            onClick={doPurge}
            disabled={!purgeConfirmed || purging}
          >
            {purging ? "Deleting…" : "Delete permanently"}
          </Button>
        </DialogActions>
      </Dialog>

      <PreviewDialog
        ui={ui}
        preview={preview}
        onClose={() => setPreview(null)}
        onDownload={() => preview?.file && download(preview.file)}
      />
    </Box>
  );
};

export default BackupPage;
